import React, { useState, useRef, useEffect } from 'react';
import useClientSearch from '../hooks/useClientSearch';

/**
 * Compact inline client search/select.
 * Search-only — no client creation allowed outside the booking flow.
 *
 * Props:
 *   selectedClient  — { client_id, full_name, primary_email } | null
 *   onSelect(client) — called when user picks a result
 *   onClear()        — called when user removes the selection
 */
export default function ClientSearch({ selectedClient, onSelect, onClear }) {
  const { query, setQuery, results, setResults, searching, search, reset } = useClientSearch();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    search(val);
    setOpen(true);
  }

  function handleSelect(c) {
    onSelect(c);
    reset();
    setOpen(false);
  }

  // ── Selected state ────────────────────────────────────────────────────────
  if (selectedClient) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
        <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
          {selectedClient.full_name?.[0]?.toUpperCase() ?? '?'}
        </div>
        <span className="text-xs font-medium text-indigo-800 flex-1 truncate">{selectedClient.full_name}</span>
        {selectedClient.primary_email && (
          <span className="text-xs text-indigo-500 truncate hidden sm:block">{selectedClient.primary_email}</span>
        )}
        <button onClick={onClear} className="text-indigo-400 hover:text-indigo-600 flex-shrink-0 ml-1" aria-label="Verwijder klant">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    );
  }

  // ── Search state ──────────────────────────────────────────────────────────
  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Zoek klant op naam of e-mail…"
          className="input text-sm pr-8"
        />
        {searching && (
          <svg className="absolute right-2.5 top-2.5 w-3.5 h-3.5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {results.map(c => (
            <li key={c.client_id}>
              <button
                onMouseDown={() => handleSelect(c)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 text-left"
              >
                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                  {c.full_name?.[0]?.toUpperCase() ?? '?'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.full_name}</p>
                  {c.primary_email && <p className="text-xs text-gray-500 truncate">{c.primary_email}</p>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && !searching && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2.5">
          <p className="text-xs text-gray-400">Geen klanten gevonden</p>
        </div>
      )}
    </div>
  );
}
